import {
  ENDPOINTS,
  RetrieveContractRequestSchema,
  type ContractIncludeSection,
  type RetrieveContractResponse,
} from '@clm/shared-types';

import { apiGet } from './client';
import { substitutePath } from './verifications';

/**
 * `retrieve-contract` — the contract record surface's only call.
 *
 * `contract_key` is the path parameter and `include` is a **repeated** query
 * key, exactly as the registry's `notes` say. Both halves are still validated
 * as one object first, because that is what `registry.ts` means by folding path,
 * query and body together: the client and the gateway cannot disagree about
 * which half a field belongs to if neither of them decides.
 *
 * ## `include` is sent empty on the first call, and that is deliberate
 *
 * `apps/api/src/routes/handlers.ts` answers `501` for any section other than
 * `full_text` — `obligations`, `clauses`, `family`, `verifications`,
 * `notifications` all need tables the pipeline does not write yet, and the
 * handler names them one at a time rather than answering with an empty array.
 * A record page that asked for them would therefore fail *entirely* rather than
 * render the contract it can have. So the first request asks for nothing
 * optional, and `full_text` — the largest field in the system — is a second
 * call behind an explicit disclosure.
 */
const RETRIEVE_ENDPOINT = ENDPOINTS['retrieve-contract'];

export async function retrieveContract(
  contract_key: string,
  include?: readonly ContractIncludeSection[],
  signal?: AbortSignal,
): Promise<RetrieveContractResponse> {
  const validated = RetrieveContractRequestSchema.parse(
    include === undefined || include.length === 0
      ? { contract_key }
      : { contract_key, include: [...include] },
  );

  const path = substitutePath(RETRIEVE_ENDPOINT.path, {
    contract_key: validated.contract_key,
  });

  const raw = await apiGet<unknown>(path, {
    // A repeated key per section. `include` is absent from the query entirely
    // when nothing optional was asked for, rather than sent as an empty value.
    ...(validated.include === undefined ? {} : { query: { include: [...validated.include] } }),
    ...(signal === undefined ? {} : { signal }),
  });

  return RETRIEVE_ENDPOINT.response.parse(raw);
}
